import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const STORAGE_KEY = "monago.sidebar.collapsed";

interface SidebarContextValue {
  // Desktop / tablet rail state. Single source of truth, persisted
  // in localStorage. Default = expanded (false). Click the
  // collapse button on the rail to flip.
  isCollapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;

  // Mobile slide-in state.
  isMobileOpen: boolean;
  openMobile: () => void;
  closeMobile: () => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setCollapsedState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  });
  const [isMobileOpen, setMobileOpen] = useState(false);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setCollapsedState(collapsed);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // Quota / private mode — non-fatal.
    }
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsedState((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Non-fatal.
      }
      return next;
    });
  }, []);

  const openMobile = useCallback(() => {
    setMobileOpen(true);
    document.body.style.overflow = "hidden";
  }, []);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    document.body.style.overflow = "";
  }, []);

  useEffect(() => {
    if (!isMobileOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobile();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isMobileOpen, closeMobile]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const value = useMemo<SidebarContextValue>(
    () => ({
      isCollapsed,
      setCollapsed,
      toggleCollapsed,
      isMobileOpen,
      openMobile,
      closeMobile,
    }),
    [
      isCollapsed,
      setCollapsed,
      toggleCollapsed,
      isMobileOpen,
      openMobile,
      closeMobile,
    ]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return ctx;
}
